import { z } from 'zod';
import {
  PositiveDecimalString,
  NonNegativeDecimalString,
  PaginationQuerySchema,
  IsoDateString,
} from './common.js';

/**
 * PCC (Passive Compensation Card / 推广卡) schemas.
 *
 * PccCard is an admin-managed limited-issue product catalog. A user purchases
 * a card by spending UO equal to the card's `price`, which creates an
 * immutable `UserPccHolding` row. Holdings accumulate EP `earnings` over time
 * through admin-triggered settlement runs that credit the user's EP bucket
 * via LedgerService.
 *
 * Cards are archived (not deleted) by flipping `is_active=false` or
 * `is_settled=true`. Once settled, no further sales or earnings are permitted
 * on the card — this is the legacy `status=1` (已结算) semantic mapped onto
 * a boolean.
 */

// ---- PccCard admin CRUD -----------------------------------------------------

export const PccCardCreateSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'must be lowercase alphanumeric + underscore'),
  title_zh: z.string().min(1).max(200),
  title_en: z.string().min(1).max(200),
  description_zh: z.string().min(1),
  description_en: z.string().min(1),
  image_url: z.string().url().max(1024).optional(),
  /** Cost per card in UO (decimal string, > 0). */
  price: PositiveDecimalString,
  /**
   * Earnings multiplier applied to matched binary-tree volume at settlement.
   * Dimensionless, e.g. '0.05' = 5%. Defaults to 0 for a placeholder card
   * that admins will set before enabling.
   */
  pcc_rate: NonNegativeDecimalString.default('0'),
  /** Total issued supply. 0 = unlimited. */
  total_count: z.coerce.number().int().min(0).default(0),
  /** Per-user purchase cap. 0 = unlimited. */
  quota: z.coerce.number().int().min(0).default(0),
  /** Optional sale window start (ISO 8601). Null = no lower bound. */
  start_time: IsoDateString.optional(),
  /** Optional sale window end (ISO 8601). Null = no upper bound. */
  end_time: IsoDateString.optional(),
  is_active: z.coerce.boolean().default(true),
  sort: z.coerce.number().int().default(0),
});
export type PccCardCreateRequest = z.infer<typeof PccCardCreateSchema>;

/**
 * Patch schema — omits `code` (immutable slug) and `total_count` (supply is
 * frozen at creation to prevent silent over-issuance). `residue_count` is
 * derived from purchases and cannot be patched either.
 */
export const PccCardPatchSchema = PccCardCreateSchema.partial().omit({
  code: true,
  total_count: true,
});
export type PccCardPatchRequest = z.infer<typeof PccCardPatchSchema>;

export const PccCardListQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  is_settled: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type PccCardListQuery = z.infer<typeof PccCardListQuerySchema>;

// ---- User purchase ----------------------------------------------------------

export const PurchasePccSchema = z.object({
  card_id: z.coerce.number().int().positive(),
  /**
   * Number of cards to purchase in this request. 1 is the common case; bulk
   * buys respect both `quota` (per-user cap) and `residue_count` (global cap).
   */
  quantity: z.coerce.number().int().min(1).max(1000).default(1),
});
export type PurchasePccRequest = z.infer<typeof PurchasePccSchema>;

// ---- Holdings list ----------------------------------------------------------

export const PccHoldingListQuerySchema = PaginationQuerySchema.extend({
  card_id: z.coerce.number().int().positive().optional(),
  /** Admin-only: restrict to a specific user id. Ignored for user-scoped calls. */
  user_id: z.coerce.number().int().positive().optional(),
});
export type PccHoldingListQuery = z.infer<typeof PccHoldingListQuerySchema>;

// ---- Admin settlement -------------------------------------------------------

/**
 * Admin settlement payload. Executes a batch payout against every holding
 * of the target card:
 *   - `per_holding_earnings` is the flat EP amount credited to each holding
 *     in the run (common case: admin has computed match_volume × pcc_rate
 *     off-platform and enters the result)
 *   - `mark_settled=true` archives the card after the payout, preventing
 *     future settlement runs or purchases on the card
 *
 * The backend walks every holding of the card, credits `per_holding_earnings`
 * via LedgerService under the `pcc_earnings` action, increments the holding's
 * `earnings` + `settle_count`, and stamps `last_settle_at`. All inside one
 * serializable transaction.
 */
export const PccSettleSchema = z.object({
  per_holding_earnings: PositiveDecimalString,
  mark_settled: z.coerce.boolean().default(false),
  note: z.string().max(200).optional(),
});
export type PccSettleRequest = z.infer<typeof PccSettleSchema>;
